import { TOOLS } from '@/constants'
import { RADAR_MODULE_SCHEMA, RADAR_STORE_SOURCED_KPIS, RADAR_TOOL_STORE_MAP, moduleSchemaHasTotal } from '@/store/utils/radarSchema.js'
import { hasTemplateViewPermission, resolveCurrentUserRoleContext } from '@/store/utils/templatePermissions.js'

/** Valores de severity aceptados del API; cualquier otro valor usa el del schema. */
const RADAR_VALID_SEVERITIES = new Set(['critical', 'warning', 'info'])

/** Devuelve la severity definida en el schema del KPI, o null si no es válida. */
const getSchemaKpiSeverity = (kpiSchema) => {
    if (!kpiSchema || kpiSchema.severity == null || kpiSchema.severity === '') {
        return null
    }

    const schemaSeverity = String(kpiSchema.severity).toLowerCase()
    const result = RADAR_VALID_SEVERITIES.has(schemaSeverity) ? schemaSeverity : null

    return result
}

/**
 * Resuelve la severity de un KPI: usa la del API solo si es 'critical', 'warning' o 'info';
 * en caso contrario (ausente o desconocida) usa la definida en RADAR_MODULE_SCHEMA.
 */
const resolveKpiSeverity = (apiSeverity, kpiSchema) => {
    const schemaSeverity = getSchemaKpiSeverity(kpiSchema)

    if (apiSeverity == null || apiSeverity === '') {
        return schemaSeverity
    }

    const normalizedApiSeverity = String(apiSeverity).toLowerCase()

    if (RADAR_VALID_SEVERITIES.has(normalizedApiSeverity)) {
        return normalizedApiSeverity
    }

    return schemaSeverity
}

/** Estrategias para calcular count a partir de un KPI crudo del API (formato kpi_array). */
const RADAR_COUNT_STRATEGIES = {
    templates_length: (rawKpi) => {
        return rawKpi && Array.isArray(rawKpi.templates) ? rawKpi.templates.length : 0
    },
    templates_total_sum: (rawKpi) => {
        if (!rawKpi || !Array.isArray(rawKpi.templates)) {
            return 0
        }
        return rawKpi.templates.reduce((sum, template) => {
            return sum + (Number(template && template.value) || 0)
        }, 0)
    }
}

/** Extrae el shape del state de un KPI a partir de su definición en el schema. */
const pickKpiStateFromSchema = (kpiSchema) => {
    const result = {
        count: Number(kpiSchema && kpiSchema.count) || 0,
        severity: getSchemaKpiSeverity(kpiSchema)
    }

    return result
}

/**
 * Crea el estado vacío de un módulo a partir del esquema (solo valores dinámicos).
 * Devuelve `null` si `toolCode` no tiene entrada en RADAR_MODULE_SCHEMA.
 */
const createEmptyModuleState = (toolCode, kpiKeys) => {
    const schema = RADAR_MODULE_SCHEMA[toolCode]

    if (!schema) {
        return null
    }

    const kpis = {}

    kpiKeys.forEach((kpiKey) => {
        const kpiSchema = schema.kpis[kpiKey]

        if (kpiSchema) {
            kpis[kpiKey] = pickKpiStateFromSchema(kpiSchema)
        }
    })

    if (moduleSchemaHasTotal(schema)) {
        const result = {
            total: Number(schema.total) || 0,
            kpis
        }

        return result
    }

    const result = { kpis }

    return result
}

/** Indexa un array de KPIs del API por su propiedad key. */
const indexApiKpisByKey = (items) => {
    if (!Array.isArray(items)) {
        return {}
    }

    const indexed = {}

    for (const item of items) {
        if (item && item.key) {
            indexed[item.key] = item
        }
    }

    return indexed
}

/** Calcula el count de un KPI crudo según la estrategia del módulo. */
const extractKpiCount = (rawKpi, countStrategy) => {
    const countFrom = RADAR_COUNT_STRATEGIES[countStrategy]

    if (!countFrom) {
        return 0
    }

    const result = countFrom(rawKpi)

    return result
}

/**
 * Este helper extrae el id de la entrada cruda del array `templates`.
 */
const extractTemplateEntryId = (templateEntry) => {
    if (templateEntry == null) {
        return null
    }

    if (typeof templateEntry === 'object') {
        const entryId = templateEntry.template_id != null ? templateEntry.template_id : templateEntry.id

        return entryId != null ? String(entryId) : null
    }

    return String(templateEntry)
}

/** Extrae el id de template de auditoría de una entrada cruda (solo action plans). */
const extractAuditTemplateEntryId = (templateEntry) => {
    if (templateEntry == null || typeof templateEntry !== 'object') {
        return null
    }

    const auditTemplateId = templateEntry.audit_template_id

    return auditTemplateId != null ? String(auditTemplateId) : null
}

/** Indica si getTemplateById devolvió un template real (audit devuelve `{}` si no existe). */
const isResolvedTemplate = (template) => {
    return template != null && typeof template === 'object' && Object.keys(template).length > 0
}

/**
 * Comprueba permisos de lectura sobre una entrada del array `templates` de un KPI.
 * En action plans exige permiso tanto sobre template_id (plan) como audit_template_id (auditoría).
 */
const canViewRadarKpiTemplateEntry = (templateEntry, toolCode, getTemplateById, getAuditTemplateById, userRole, employeeRol) => {
    const canView = (getter, templateId) => {
        if (templateId == null || typeof getter !== 'function') {
            return false
        }

        const template = getter(templateId)

        if (!isResolvedTemplate(template)) {
            return false
        }

        const templateConfig = Object.prototype.hasOwnProperty.call(template, 'config') ? template.config : null

        return hasTemplateViewPermission(templateConfig, userRole, employeeRol)
    }

    const templateId = extractTemplateEntryId(templateEntry)

    if (!canView(getTemplateById, templateId)) {
        return false
    }

    if (toolCode !== TOOLS.ACTION_PLANS) {
        return true
    }

    const auditTemplateId = extractAuditTemplateEntryId(templateEntry)

    return canView(getAuditTemplateById, auditTemplateId)
}

/**
 * Comprueba si el usuario puede ver una entrada del array `templates` de un KPI Radar.
 * Reutilizable fuera del pipeline de normalización (p. ej. actionplan/getOpenActionPlansByAuditTemplate).
 * Si no hay contexto de permisos, no filtra (devuelve true).
 */
const canViewRadarTemplateEntry = (templateEntry, toolCode, rootGetters) => {
    if (!rootGetters) {
        return true
    }

    const storeModule = RADAR_TOOL_STORE_MAP[toolCode]

    if (!storeModule) {
        return true
    }

    const getTemplateById = rootGetters[`${storeModule}/getTemplateById`]
    const getAuditTemplateById = toolCode === TOOLS.ACTION_PLANS ? rootGetters['audit/getTemplateById'] : null
    const userRoleContext = resolveCurrentUserRoleContext(rootGetters)

    if (typeof getTemplateById !== 'function' || !userRoleContext) {
        return true
    }

    const { userRole, employeeRol } = userRoleContext

    return canViewRadarKpiTemplateEntry(templateEntry, toolCode, getTemplateById, getAuditTemplateById, userRole, employeeRol)
}

/** Filtra las entradas de `templates` según permisos de lectura; no muta el KPI original. */
const filterRawKpiTemplates = (rawKpi, toolCode, rootGetters) => {
    if (!rawKpi || !Array.isArray(rawKpi.templates) || !rootGetters) {
        return rawKpi
    }

    const storeModule = RADAR_TOOL_STORE_MAP[toolCode]

    if (!storeModule) {
        return rawKpi
    }

    const getTemplateById = rootGetters[`${storeModule}/getTemplateById`]

    if (typeof getTemplateById !== 'function' || !resolveCurrentUserRoleContext(rootGetters)) {
        return rawKpi
    }

    const templates = rawKpi.templates.filter((templateEntry) => {
        return canViewRadarTemplateEntry(templateEntry, toolCode, rootGetters)
    })

    return { ...rawKpi, templates }
}

/** Construye el state de un KPI a partir de los datos crudos del API. */
const pickKpiStateFromApi = (rawKpi, countStrategy, kpiSchema, toolCode, rootGetters = null) => {
    const state = {
        count: 0,
        severity: getSchemaKpiSeverity(kpiSchema)
    }

    if (!rawKpi) {
        return state
    }

    if (countStrategy) {
        const kpiForCount = filterRawKpiTemplates(rawKpi, toolCode, rootGetters)

        state.count = extractKpiCount(kpiForCount, countStrategy)
    } else {
        state.count = Number(rawKpi.value) || 0
    }

    state.severity = resolveKpiSeverity(rawKpi.severity, kpiSchema)

    return state
}

/** Devuelve el KPI con count actualizado; preserva severity. */
const patchKpiCount = (kpiState, count) => {
    const result = {
        ...kpiState,
        count: Number(count) || 0
    }

    return result
}

/** Normaliza un módulo con formato kpi_array (tasks, registers, issues, action plans). */
const normalizeKpiArrayModule = (toolCode, rawModuleData, moduleSchema, kpiKeys, rootGetters = null) => {
    const moduleState = createEmptyModuleState(toolCode, kpiKeys)

    if (!moduleState) {
        return null
    }

    const rawKpisByKey = indexApiKpisByKey(rawModuleData)

    kpiKeys.forEach((kpiKey) => {
        const kpiSchema = moduleSchema.kpis[kpiKey]

        if (!kpiSchema) {
            return
        }

        const rawKpi = rawKpisByKey[kpiKey]

        moduleState.kpis[kpiKey] = pickKpiStateFromApi(rawKpi, moduleSchema.countStrategy, kpiSchema, toolCode, rootGetters)
    })

    return moduleState
}

/** Normaliza el módulo de sensores (formato sensors_object). */
const normalizeSensorsModule = (toolCode, rawModuleData, moduleSchema, kpiKeys, rootGetters = null) => {
    const moduleState = createEmptyModuleState(toolCode, kpiKeys)

    if (!moduleState) {
        return null
    }

    moduleState.total = Number(rawModuleData && rawModuleData.total) || 0

    const rawKpisByKey = indexApiKpisByKey(rawModuleData && rawModuleData.kpis)

    kpiKeys.forEach((kpiKey) => {
        const kpiSchema = moduleSchema.kpis[kpiKey]

        if (!kpiSchema) {
            return
        }

        const rawKpi = rawKpisByKey[kpiKey]

        moduleState.kpis[kpiKey] = pickKpiStateFromApi(rawKpi, null, kpiSchema, toolCode, rootGetters)
    })

    return moduleState
}

/** Normaliza radarKPIs crudos al shape consumido por Radar. */
const normalizeRadarKPIs = (toolCode, radarKPIs, kpiKeys, rootGetters = null) => {
    const moduleSchema = RADAR_MODULE_SCHEMA[toolCode]

    if (!moduleSchema) {
        return null
    }

    if (radarKPIs === undefined || radarKPIs === null) {
        const result = createEmptyModuleState(toolCode, kpiKeys)

        return result
    }

    if (moduleSchema.apiFormat === 'sensors_object') {
        const result = normalizeSensorsModule(toolCode, radarKPIs, moduleSchema, kpiKeys, rootGetters)

        return result
    }

    const result = normalizeKpiArrayModule(toolCode, radarKPIs, moduleSchema, kpiKeys, rootGetters)

    return result
}

/**
 * Devuelve counts en 0 para los KPIs obtenidos de otros módulos de la store.
 * @param {number} toolCode
 * @param {string[]} kpiKeys
 * @returns {Record<string, number>}
 */
const buildDefaultLocalKpiCounts = (toolCode, kpiKeys) => {
    const storeSourced = RADAR_STORE_SOURCED_KPIS[toolCode] || []
    const counts = {}

    kpiKeys.forEach((kpiKey) => {
        if (storeSourced.includes(kpiKey)) {
            counts[kpiKey] = 0
        }
    })

    return counts
}

/**
 * Transforma radarKPIs al formato consumido por Radar.
 * @param {number} toolCode
 * @param {object|array|null} radarKPIs — respuesta cruda del endpoint /radar
 * @param {string[]} kpiKeys — claves de KPI solicitadas
 * @param {Record<string, number>} [localKpiCounts] — counts de KPIs sincronizados localmente
 * @param {object|null} [rootGetters] — getters raíz para permisos de templates vía getTemplateById
 * @returns {{ kpis: Record<string, { count: number, severity: string|null }>, total?: number }|null}
 */
const buildRadarModuleState = (toolCode, radarKPIs, kpiKeys, localKpiCounts = {}, rootGetters = null) => {
    const moduleState = normalizeRadarKPIs(toolCode, radarKPIs, kpiKeys, rootGetters)

    if (!moduleState) {
        return null
    }

    Object.entries(localKpiCounts).forEach(([kpiKey, count]) => {
        if (!moduleState.kpis[kpiKey]) {
            return
        }

        moduleState.kpis[kpiKey] = patchKpiCount(moduleState.kpis[kpiKey], count)
    })

    return moduleState
}

/**
 * Establece (crea o reemplaza por completo), de forma inmutable, el listado `templates` de un
 * KPI concreto dentro de los radarKPIs crudos (formato kpi_array).
 *
 * Reemplaza el array entero: se usa para KPIs que el frontend recalcula completos a partir de datos
 * ya cargados en la store (p.ej. tasks_to_justify_today BUILD-7281, tasks_to_justify_yesterday
 * BUILD-7404), sin depender del API para ese KPI.
 *
 * @param {array|null} radarKPIs — radarKPIs crudos del módulo
 * @param {string} kpiKey — key del KPI a establecer (p.ej. 'tasks_to_justify_today')
 * @param {array} templates — nuevo listado completo de templates de ese KPI
 * @returns {array} nuevo array de radarKPIs (crea la entrada del KPI si no existía)
 */
const setTemplatesForRadarKpi = (radarKPIs, kpiKey, templates) => {
    const baseRadarKPIs = Array.isArray(radarKPIs) ? radarKPIs : []
    let found = false

    const updatedRadarKPIs = baseRadarKPIs.map((rawKpi) => {
        if (!rawKpi || rawKpi.key !== kpiKey) {
            return rawKpi
        }

        found = true

        return { ...rawKpi, templates }
    })

    if (found) {
        return updatedRadarKPIs
    }

    return [...updatedRadarKPIs, { key: kpiKey, templates }]
}

/** Compara dos listados de template ids sin importar el orden (para evitar commits redundantes). */
const haveSameTemplateIds = (templatesA, templatesB) => {
    if (!Array.isArray(templatesA) || !Array.isArray(templatesB) || templatesA.length !== templatesB.length) {
        return false
    }

    const sortedA = templatesA.map(String).sort()
    const sortedB = templatesB.map(String).sort()

    return sortedA.every((id, index) => id === sortedB[index])
}

export { buildDefaultLocalKpiCounts, buildRadarModuleState, canViewRadarTemplateEntry, haveSameTemplateIds, setTemplatesForRadarKpi }
